// frontend/src/components/JobMessagesAccordian.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-11-24
// Updated:      2026-09-14 11:06 BST
//
// Description:
//   Canonical hierarchy-aware messaging-history accordion.
//   • Retains the historical `JobMessagesAccordian` component/file name so existing
//     JobEditModal imports remain valid.
//   • Supports message history at Job, Contact or Customer scope.
//   • Legacy automatic scope precedence remains Job -> Contact -> Customer.
//   • Explicit history scope may be supplied independently via:
//       - historyScopeType = "job" | "contact" | "customer"
//       - historyScopeId
//   • recipientContactId is independent from history scope and is used only for
//     NEW outbound-message recipient context.
//   • Uses the corresponding flattened MessageRead endpoint:
//       - /api/conversations/job/{job_id}/messages
//       - /api/conversations/contact/{contact_id}/messages
//       - /api/conversations/customer/{customer_id}/messages
//   • New message actions delegate to the parent-owned canonical channel flows:
//       - EmailMessageModal
//       - SmsMessageModal
//       - WhatsAppTemplateSelectorModal for a NEW WhatsApp conversation
//       - WhatsAppMessageModal for an EXISTING WhatsApp thread
//   • Clicking a history row passes the exact conversation/message identity back
//     to the parent so the channel modal can open that specific thread.
//
// Messaging Hierarchy (2026-09-10):
//   • Job-related conversation:
//       job_id + contact_id + customer_id where those associations are known.
//   • Direct contact conversation:
//       contact_id + customer_id with job_id = null.
//   • Customer history:
//       customer_id, spanning all conversations belonging to that customer.
//   • History scope and outbound recipient are deliberately separate:
//       - historyScopeType/historyScopeId choose which history endpoint is loaded.
//       - recipientContactId chooses the recipient for a NEW outbound message.
//   • At Customer scope, callers should pass recipientContactId (normally the selected
//     or primary contact). That recipient NEVER narrows or rewrites customer history.
//   • contactId remains supported for legacy callers and association context.
//
// Mobile Responsiveness (2026-09-10):
//   • Desktop retains the existing history table presentation.
//   • Mobile transforms the SAME table rows into labelled message cards; no duplicate
//     message data tree is mounted.
//   • New-message controls stack cleanly and use 44px touch-safe channel buttons.
//   • History heading/filter stack on narrow screens.
//   • Mobile history shows one complete message card per internal vertical scroll/snap.
//   • Long addresses, subjects, previews and status values wrap safely.
//   • Only the internal history viewport is sized; ancestor widget/page geometry is
//     not mutated.
//
// Compatibility Notes:
//   • Existing callers that only pass jobId continue to work unchanged.
//   • Existing callers using jobId/contactId/customerId without an explicit history
//     scope keep the historical Job -> Contact -> Customer precedence.
//   • Callback payloads expose canonical camelCase keys AND the historical snake_case
//     aliases during the consolidation so old and new parent handlers can coexist.
//   • No tenant_id is accepted, inferred or emitted by this component.
//   • Tenant scope remains backend-owned via authenticated user context.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { FaEnvelope, FaSms, FaWhatsapp } from "react-icons/fa";

const extractHttpError = (e) => {
  const r = e?.response;
  if (!r) return e?.message || String(e);

  const d = r.data;
  const detail =
    (d &&
      (typeof d.detail === "string"
        ? d.detail
        : JSON.stringify(d.detail))) ||
    (typeof d === "string" ? d : JSON.stringify(d));

  return `${r.status} ${r.statusText || ""} - ${detail}`.trim();
};

// Basic HTML tag stripper for body_html fallbacks.
const stripHtml = (html) => {
  if (!html) return "";
  return String(html).replace(/<[^>]+>/g, " ");
};

const asNullableId = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return value;
};

const buildScopeConfig = (type, id) => {
  const resolvedId = asNullableId(id);

  if (type === "job") {
    return {
      type: "job",
      id: resolvedId,
      singularLabel: "job",
      title: "Job Messages",
      endpoint:
        resolvedId !== null
          ? `conversations/job/${resolvedId}/messages`
          : null,
    };
  }

  if (type === "contact") {
    return {
      type: "contact",
      id: resolvedId,
      singularLabel: "contact",
      title: "Contact Messages",
      endpoint:
        resolvedId !== null
          ? `conversations/contact/${resolvedId}/messages`
          : null,
    };
  }

  if (type === "customer") {
    return {
      type: "customer",
      id: resolvedId,
      singularLabel: "customer",
      title: "Customer Messages",
      endpoint:
        resolvedId !== null
          ? `conversations/customer/${resolvedId}/messages`
          : null,
    };
  }

  return {
    type: null,
    id: null,
    singularLabel: "record",
    title: "Messages",
    endpoint: null,
  };
};

const getScopeConfig = ({
  historyScopeType,
  historyScopeId,
  jobId,
  contactId,
  customerId,
}) => {
  const explicitType = ["job", "contact", "customer"].includes(
    String(historyScopeType || "").toLowerCase()
  )
    ? String(historyScopeType).toLowerCase()
    : null;

  if (explicitType) {
    const explicitId =
      asNullableId(historyScopeId) ??
      (explicitType === "job"
        ? asNullableId(jobId)
        : explicitType === "contact"
        ? asNullableId(contactId)
        : asNullableId(customerId));

    // Explicit scope is authoritative. If its ID is missing, fail closed rather
    // than silently falling through to a different scope supplied for context.
    return buildScopeConfig(explicitType, explicitId);
  }

  // Backwards-compatible automatic scope selection.
  if (asNullableId(jobId) !== null) {
    return buildScopeConfig("job", jobId);
  }

  if (asNullableId(contactId) !== null) {
    return buildScopeConfig("contact", contactId);
  }

  if (asNullableId(customerId) !== null) {
    return buildScopeConfig("customer", customerId);
  }

  return buildScopeConfig(null, null);
};

const JobMessagesAccordian = ({
  jobId = null,
  contactId = null,
  customerId = null,
  historyScopeType = null,
  historyScopeId = null,
  recipientContactId = null,
  defaultOpen = false,
  onNewEmail,
  onNewSms,
  onNewWhatsApp,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const historyViewportRef = useRef(null);
  const firstMessageRowRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [messages, setMessages] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all"); // all | open | closed

  const scope = useMemo(
    () =>
      getScopeConfig({
        historyScopeType,
        historyScopeId,
        jobId,
        contactId,
        customerId,
      }),
    [
      historyScopeType,
      historyScopeId,
      jobId,
      contactId,
      customerId,
    ]
  );

  // History-context fallbacks are intentionally separate from the recipient of a
  // NEW outbound message. In particular, Customer history must never stamp the
  // selected/primary recipient contact onto unrelated historical rows.
  const historyFallbackJobId =
    scope.type === "job" ? scope.id : null;

  const historyFallbackContactId =
    scope.type === "contact"
      ? scope.id
      : scope.type === "job"
      ? asNullableId(contactId)
      : null;

  const historyFallbackCustomerId =
    scope.type === "customer"
      ? scope.id
      : asNullableId(customerId);

  const effectiveJobId =
    asNullableId(jobId) ??
    (scope.type === "job" ? scope.id : null);

  const effectiveRecipientContactId =
    asNullableId(recipientContactId) ??
    asNullableId(contactId) ??
    (scope.type === "contact" ? scope.id : null);

  const effectiveCustomerId =
    asNullableId(customerId) ??
    (scope.type === "customer" ? scope.id : null);

  const panelId = useMemo(() => {
    const type = scope.type || "unscoped";
    const id = scope.id ?? "none";
    return `messages-panel-${type}-${id}`;
  }, [scope.type, scope.id]);

  const canStartNewMessage =
    scope.type === "customer"
      ? effectiveRecipientContactId !== null
      : effectiveJobId !== null ||
        effectiveRecipientContactId !== null;

  useEffect(() => {
    if (!onNewEmail && !onNewSms && !onNewWhatsApp) {
      console.warn(
        "[JobMessagesAccordian] No channel handlers supplied; " +
          "new-message buttons and history-row clicks cannot open messaging modals."
      );
    }
  }, [onNewEmail, onNewSms, onNewWhatsApp]);

  useEffect(() => {
    if (!open || !scope.endpoint) {
      if (!scope.endpoint) {
        setMessages([]);
        setErr("");
      }
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setErr("");

      try {
        const { data } = await axiosInstance.get(scope.endpoint);

        if (cancelled) return;

        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
          ? data.items
          : [];

        if (rows.length) {
          console.info(
            `[JobMessagesAccordian] raw ${scope.type}-scoped message payload`,
            {
              scopeType: scope.type,
              scopeId: scope.id,
              rows: rows.slice(0, 10),
            }
          );
        } else {
          console.info(
            `[JobMessagesAccordian] empty ${scope.type}-scoped message payload`,
            {
              scopeType: scope.type,
              scopeId: scope.id,
            }
          );
        }

        // Normalise flattened MessageRead rows while retaining any association
        // fields a newer backend returns. Props remain the fallback context.
        const normalised = rows
          .map((m) => {
            const channel = m.channel || "unknown";

            const direction =
              m.direction && typeof m.direction === "string"
                ? m.direction.toLowerCase()
                : m.direction || "unknown";

            let to_display = "";
            let from_display = "";

            const meta =
              m.metadata_json && typeof m.metadata_json === "object"
                ? m.metadata_json
                : null;

            if (meta) {
              to_display =
                meta.to ||
                meta.to_email ||
                meta.to_address ||
                meta.to_phone ||
                meta.to_phone_normalised ||
                meta.to_whatsapp_normalised ||
                "";

              from_display =
                meta.from ||
                meta.from_email ||
                meta.from_email_address ||
                meta.from_address ||
                meta.from_number ||
                meta.from_phone_normalised ||
                "";
            }

            const subject = m.subject || "";

            const previewCandidate =
              m.preview ||
              m.body_text ||
              stripHtml(m.body_html) ||
              "";

            const preview = previewCandidate
              ? String(previewCandidate).slice(0, 200)
              : "";

            const created_at =
              m.created_at ||
              m.sent_at ||
              m.received_at ||
              m.last_message_at ||
              null;

            const delivery_status = m.delivery_status || "";
            const conversation_status = m.conversation_status || "";
            const status = delivery_status || conversation_status || "";

            return {
              id: m.id ?? m.message_id ?? null,
              conversation_id:
                m.conversation_id ?? m.conversationId ?? null,
              job_id:
                m.job_id ??
                m.jobId ??
                historyFallbackJobId ??
                null,
              contact_id:
                m.contact_id ??
                m.contactId ??
                historyFallbackContactId ??
                null,
              customer_id:
                m.customer_id ??
                m.customerId ??
                historyFallbackCustomerId ??
                null,
              channel,
              direction,
              subject,
              to_display,
              from_display,
              preview,
              created_at,
              status,
              conversation_status,
            };
          })
          .sort((a, b) => {
            const da = a.created_at
              ? new Date(a.created_at).getTime()
              : 0;
            const db = b.created_at
              ? new Date(b.created_at).getTime()
              : 0;

            // newest first
            return db - da;
          });

        console.info(
          `[JobMessagesAccordian] normalised ${scope.type}-scoped messages`,
          {
            scopeType: scope.type,
            scopeId: scope.id,
            count: normalised.length,
          }
        );

        setMessages(normalised);
      } catch (e) {
        if (!cancelled) {
          setErr(extractHttpError(e));
          setMessages([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    scope.endpoint,
    scope.id,
    scope.type,
    historyFallbackJobId,
    historyFallbackContactId,
    historyFallbackCustomerId,
  ]);

  const formatDateGB = (value) => {
    if (!value) return "";

    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";

      return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const directionBadgeClass = (dir) => {
    const d = String(dir || "").toLowerCase();

    if (d === "inbound") {
      return "bg-green-100 text-green-700 border border-green-300";
    }

    if (d === "outbound") {
      return "bg-blue-100 text-blue-700 border border-blue-300";
    }

    return "bg-gray-100 text-gray-600 border border-gray-300";
  };

  const channelLabel = (ch) => {
    const c = String(ch || "").toLowerCase();

    if (c === "email") return "Email";
    if (c === "sms") return "SMS";
    if (c === "whatsapp") return "WhatsApp";

    return ch || "Unknown";
  };

  const filteredMessages =
    statusFilter === "all"
      ? messages
      : messages.filter((m) => {
          const cs = String(
            m.conversation_status || m.status || ""
          ).toLowerCase();

          if (statusFilter === "open") return cs === "open";
          if (statusFilter === "closed") return cs === "closed";

          return true;
        });

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const viewport = historyViewportRef.current;
    const mediaQuery = window.matchMedia("(max-width: 767px)");

    if (!viewport) {
      return undefined;
    }

    let animationFrame = 0;

    const applyViewportHeight = () => {
      cancelAnimationFrame(animationFrame);

      viewport.style.height = "";
      viewport.style.minHeight = "";

      if (!mediaQuery.matches || !open || loading) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const firstRow = firstMessageRowRef.current;

        if (!firstRow) {
          return;
        }

        const rowHeight = Math.ceil(
          Math.max(
            firstRow.getBoundingClientRect().height || 0,
            firstRow.scrollHeight || 0
          )
        );

        if (!Number.isFinite(rowHeight) || rowHeight <= 0) {
          return;
        }

        const viewportHeight = rowHeight + 10;
        viewport.style.height = `${viewportHeight}px`;
        viewport.style.minHeight = `${viewportHeight}px`;
      });
    };

    applyViewportHeight();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyViewportHeight())
        : null;

    if (resizeObserver && firstMessageRowRef.current) {
      resizeObserver.observe(firstMessageRowRef.current);
    }

    const handleMediaChange = () => applyViewportHeight();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyViewportHeight);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyViewportHeight);

      viewport.style.height = "";
      viewport.style.minHeight = "";
    };
  }, [
    filteredMessages.length,
    loading,
    open,
    statusFilter,
  ]);

  const buildPayload = ({
    message = null,
    channel,
    mode,
  }) => {
    const isNewMessage = mode === "new";

    const resolvedJobId = isNewMessage
      ? effectiveJobId
      : message?.job_id ??
        message?.jobId ??
        historyFallbackJobId ??
        null;

    const resolvedContactId = isNewMessage
      ? effectiveRecipientContactId
      : message?.contact_id ??
        message?.contactId ??
        historyFallbackContactId ??
        null;

    const resolvedCustomerId = isNewMessage
      ? effectiveCustomerId
      : message?.customer_id ??
        message?.customerId ??
        historyFallbackCustomerId ??
        null;

    const resolvedConversationId =
      message?.conversation_id ??
      message?.conversationId ??
      null;

    const resolvedMessageId =
      message?.id ??
      message?.message_id ??
      message?.messageId ??
      null;

    return {
      // Canonical consolidation contract.
      jobId: resolvedJobId,
      contactId: resolvedContactId,
      customerId: resolvedCustomerId,
      conversationId: resolvedConversationId,
      messageId: resolvedMessageId,
      channel,
      mode,

      // Temporary compatibility aliases for existing JobEditModal handlers.
      job_id: resolvedJobId,
      contact_id: resolvedContactId,
      customer_id: resolvedCustomerId,
      conversation_id: resolvedConversationId,
      message_id: resolvedMessageId,
    };
  };

  const dispatchChannel = (payload) => {
    if (payload.channel === "email" && onNewEmail) {
      onNewEmail(payload);
      return;
    }

    if (payload.channel === "sms" && onNewSms) {
      onNewSms(payload);
      return;
    }

    if (payload.channel === "whatsapp" && onNewWhatsApp) {
      onNewWhatsApp(payload);
      return;
    }

    console.info(
      "[JobMessagesAccordian] Channel action has no supported handler",
      payload
    );
  };

  const handleRowClick = (message) => {
    const channel = String(message.channel || "").toLowerCase();

    const payload = buildPayload({
      message,
      channel,
      mode: "thread",
    });

    console.info(
      "[JobMessagesAccordian] history row selected",
      payload
    );

    dispatchChannel(payload);
  };

  const handleNewMessage = (channel) => {
    if (!canStartNewMessage) {
      return;
    }

    const payload = buildPayload({
      channel,
      mode: "new",
    });

    dispatchChannel(payload);
  };

  const newMessageHint = canStartNewMessage
    ? scope.type === "job"
      ? "Choose a channel to send a new message about this job."
      : scope.type === "contact"
      ? "Choose a channel to send a new message to this contact."
      : "Choose a channel to send a new message to the selected customer contact."
    : scope.type === "customer"
    ? "Select a customer contact before starting a new message."
    : "A contact is required before starting a new message.";

  const historyHeading = scope.type
    ? `Message history for this ${scope.singularLabel}`
    : "Message history";

  return (
    <div className="vx-messages-accordion overflow-hidden rounded-xl border border-gray-200">
      <style>{`
        @media (max-width: 767px) {
          .vx-messages-history {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            scroll-snap-type: y mandatory;
          }

          .vx-messages-history::-webkit-scrollbar {
            display: none;
          }

          .vx-messages-table,
          .vx-messages-table tbody {
            display: block;
            width: 100%;
            min-width: 0;
          }

          .vx-messages-table thead {
            display: none;
          }

          .vx-messages-table tr.vx-message-row {
            display: grid;
            width: 100%;
            min-width: 0;
            box-sizing: border-box;
            margin: 0 0 8px 0;
            padding: 10px 12px;
            border: 1px solid #e5e7eb;
            border-radius: 10px;
            background: #fff;
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }

          .vx-messages-table tr.vx-message-row:last-child {
            margin-bottom: 0;
          }

          .vx-messages-table tr.vx-message-row td {
            display: grid;
            grid-template-columns: minmax(100px, 0.75fr) minmax(0, 1.25fr);
            gap: 10px;
            align-items: start;
            min-width: 0;
            padding: 7px 0 !important;
            border-bottom: 1px solid #f1f5f9;
            white-space: normal !important;
            text-align: left !important;
            overflow-wrap: anywhere;
          }

          .vx-messages-table tr.vx-message-row td:last-child {
            border-bottom: 0;
          }

          .vx-messages-table tr.vx-message-row td::before {
            content: attr(data-label);
            min-width: 0;
            font-size: 12px;
            font-weight: 700;
            color: #6b7280;
            overflow-wrap: anywhere;
          }

          .vx-messages-table tr.vx-message-state-row {
            display: table-row;
          }

          .vx-messages-table tr.vx-message-state-row td {
            display: table-cell;
            width: 100%;
            padding: 12px !important;
          }

          .vx-messages-table tr.vx-message-state-row td::before {
            content: none;
          }

          .vx-message-preview {
            max-width: none !important;
            white-space: normal !important;
            overflow: visible !important;
            text-overflow: clip !important;
          }
        }
      `}</style>

      {/* Header */}
      <button
        type="button"
        className="flex min-h-[48px] w-full touch-manipulation items-center justify-between gap-3 bg-btnGeneralNormal px-4 py-3 text-left text-sm font-semibold text-white"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={panelId}
      >
        <span className="min-w-0 break-words">{scope.title}</span>
        <span className="ml-2 flex-none" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
      </button>

      {open && (
        <div
          id={panelId}
          className="space-y-4 border-t border-gray-200 bg-white p-3 sm:p-4"
        >
          {!scope.endpoint && (
            <div className="break-words rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Message history cannot be loaded because no job, contact or customer scope was supplied.
            </div>
          )}

          {/* New message row */}
          <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold">New message</div>
              <div className="break-words text-xs text-gray-600">
                {newMessageHint}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-xl sm:flex sm:items-center sm:gap-3">
              <button
                type="button"
                onClick={() => handleNewMessage("email")}
                disabled={!canStartNewMessage || !onNewEmail}
                className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-btnActionNormal text-white shadow hover:bg-btnActionHover active:bg-btnActionClick disabled:cursor-not-allowed disabled:opacity-40"
                title={
                  canStartNewMessage
                    ? "New Email"
                    : "Select a contact before sending email"
                }
                aria-label="New Email"
              >
                <FaEnvelope />
              </button>

              <button
                type="button"
                onClick={() => handleNewMessage("sms")}
                disabled={!canStartNewMessage || !onNewSms}
                className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-btnGeneralNormal text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick disabled:cursor-not-allowed disabled:opacity-40"
                title={
                  canStartNewMessage
                    ? "New SMS"
                    : "Select a contact before sending SMS"
                }
                aria-label="New SMS"
              >
                <FaSms />
              </button>

              <button
                type="button"
                onClick={() => handleNewMessage("whatsapp")}
                disabled={!canStartNewMessage || !onNewWhatsApp}
                className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-full bg-green-600 text-white shadow hover:bg-green-700 active:bg-green-800 disabled:cursor-not-allowed disabled:opacity-40"
                title={
                  canStartNewMessage
                    ? "New WhatsApp"
                    : "Select a contact before sending WhatsApp"
                }
                aria-label="New WhatsApp"
              >
                <FaWhatsapp />
              </button>
            </div>
          </div>

          {/* Errors */}
          {err && (
            <div className="break-words rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
              ❌ {err}
            </div>
          )}

          {/* History */}
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <div className="flex flex-col gap-2 border-b border-gray-200 bg-white px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4 sm:py-2">
              <div className="min-w-0 break-words text-sm font-semibold">
                {historyHeading}
              </div>

              <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-2 text-xs text-gray-700 sm:flex">
                <span>Filter:</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="min-h-[44px] min-w-0 rounded border border-gray-300 bg-white px-3 py-2 text-xs sm:min-h-0 sm:px-2 sm:py-1"
                >
                  <option value="all">All</option>
                  <option value="open">Open only</option>
                  <option value="closed">Closed only</option>
                </select>
              </div>
            </div>

            <div
              ref={historyViewportRef}
              className="vx-messages-history overflow-auto"
            >
              <table className="vx-messages-table w-full text-left text-sm text-gray-700">
                <thead>
                  <tr className="bg-white">
                    <th className="border-b px-3 py-2">Date</th>
                    <th className="border-b px-3 py-2">Channel</th>
                    <th className="border-b px-3 py-2">Direction</th>
                    <th className="border-b px-3 py-2">From / To</th>
                    <th className="border-b px-3 py-2">Subject / Preview</th>
                    <th className="border-b px-3 py-2">Status</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr className="vx-message-state-row">
                      <td
                        colSpan={6}
                        className="px-3 py-3 text-gray-500 italic"
                      >
                        Loading messages…
                      </td>
                    </tr>
                  ) : filteredMessages.length === 0 ? (
                    <tr className="vx-message-state-row">
                      <td
                        colSpan={6}
                        className="px-3 py-3 text-gray-500 italic"
                      >
                        {scope.type
                          ? `No messages recorded for this ${scope.singularLabel} yet.`
                          : "No messages available."}
                      </td>
                    </tr>
                  ) : (
                    filteredMessages.map((message, index) => (
                      <tr
                        key={
                          message.id ??
                          `${message.conversation_id || "conversation"}-${index}`
                        }
                        ref={index === 0 ? firstMessageRowRef : null}
                        className="vx-message-row cursor-pointer odd:bg-gray-50 hover:bg-blue-50"
                        onClick={() => handleRowClick(message)}
                      >
                        <td
                          data-label="Date"
                          className="whitespace-nowrap px-3 py-2"
                        >
                          {formatDateGB(message.created_at)}
                        </td>

                        <td
                          data-label="Channel"
                          className="whitespace-nowrap px-3 py-2"
                        >
                          {channelLabel(message.channel)}
                        </td>

                        <td
                          data-label="Direction"
                          className="whitespace-nowrap px-3 py-2"
                        >
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${directionBadgeClass(
                              message.direction
                            )}`}
                          >
                            {message.direction || "unknown"}
                          </span>
                        </td>

                        <td
                          data-label="From / To"
                          className="px-3 py-2"
                        >
                          <div className="break-words text-xs text-gray-700">
                            {message.direction === "outbound" ? (
                              <>
                                <span className="font-semibold">To:</span>{" "}
                                {message.to_display || "-"}
                              </>
                            ) : (
                              <>
                                <span className="font-semibold">From:</span>{" "}
                                {message.from_display || "-"}
                              </>
                            )}
                          </div>
                        </td>

                        <td
                          data-label="Subject / Preview"
                          className="px-3 py-2"
                        >
                          <div className="break-words text-xs font-semibold text-gray-800">
                            {message.subject || "(no subject)"}
                          </div>

                          {message.preview && (
                            <div className="vx-message-preview break-words text-xs text-gray-600 md:max-w-xs md:truncate">
                              {message.preview}
                            </div>
                          )}
                        </td>

                        <td
                          data-label="Status"
                          className="whitespace-nowrap px-3 py-2"
                        >
                          <span className="break-words text-xs text-gray-700">
                            {message.status || "-"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobMessagesAccordian;
